import { useEffect, useState } from "react";
import { api } from "../services/api";
import { 
  Save, 
  Loader2, 
  Plus, 
  Trash2, 
  MessageCircle, 
  HelpCircle 
} from "lucide-react";
import "./Dashboard.css";

export default function Faq() {
  const [faqItems, setFaqItems] = useState([]);
  const [registroId, setRegistroId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const clienteId = localStorage.getItem("siteId");

  useEffect(() => {
    loadFaq();
  }, []);

  async function loadFaq() {
    if (!clienteId) return;
    
    try {
      const response = await api.get(`/faq/cliente/${clienteId}`);
      setRegistroId(response.data.id);
      setFaqItems(response.data.perguntas_respostas || []);
    } catch (error) {
      if (error.response?.status === 404) {
        setRegistroId(null);
        setFaqItems([]);
      } else {
        console.error("Erro ao carregar FAQ:", error);
      }
    } finally {
      setIsFetching(false);
    }
  }

  // ---- MANIPULAÇÃO DAS PERGUNTAS ----
  const handleAddQuestion = () => {
    setFaqItems([
      ...faqItems, 
      { id: Date.now().toString(), pergunta: "", ativo: true, ordem: faqItems.length + 1, respostas: [""] }
    ]);
  };

  const handleRemoveQuestion = (index) => {
    const newItems = [...faqItems];
    newItems.splice(index, 1);
    setFaqItems(newItems);
  };

  const handleChangeQuestion = (index, field, value) => {
    const newItems = [...faqItems];
    newItems[index][field] = value;
    setFaqItems(newItems);
  };

  // ---- MANIPULAÇÃO DAS RESPOSTAS (dentro da pergunta) ----
  const handleAddAnswer = (qIndex) => {
    const newItems = [...faqItems];
    newItems[qIndex].respostas.push("");
    setFaqItems(newItems);
  };

  const handleRemoveAnswer = (qIndex, aIndex) => {
    const newItems = [...faqItems];
    newItems[qIndex].respostas.splice(aIndex, 1);
    setFaqItems(newItems);
  };

  const handleChangeAnswer = (qIndex, aIndex, value) => {
    const newItems = [...faqItems];
    newItems[qIndex].respostas[aIndex] = value;
    setFaqItems(newItems);
  };

  // ---- SALVAR NO BANCO ----
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const payload = {
        cliente_id: clienteId,
        perguntas_respostas: faqItems
      };

      if (registroId) {
        await api.put(`/faq/${registroId}`, payload);
      } else {
        const response = await api.post("/faq", payload);
        setRegistroId(response.data.id);
      }
      alert("FAQ salvo com sucesso!");
    } catch (error) {
      console.error(error);
      alert("Erro ao salvar o FAQ.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="dashboard-loading">
        <Loader2 className="spinner" size={40} />
        <p>Carregando FAQ...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>Perguntas Frequentes (FAQ)</h1>
            <p>Gerencie as dúvidas comuns e suas respostas.</p>
          </div>
          <button className="btn btn-secondary" onClick={handleAddQuestion}>
            <Plus size={18} /> Nova Pergunta
          </button>
        </div>

        <div className="config-card">
          <div className="section-title">
            <HelpCircle size={18} />
            <h3>Lista de Perguntas</h3>
          </div>

          {faqItems.length === 0 ? (
            <p style={{ color: "var(--color-text-light)", textAlign: "center", padding: "2rem 0" }}>
              Nenhuma pergunta cadastrada. Clique em "Nova Pergunta" para iniciar.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {faqItems.map((item, qIndex) => (
                <div key={item.id} style={{ border: "1px solid var(--color-border)", padding: "1.5rem", borderRadius: "var(--radius)" }}>
                  
                  {/* Cabeçalho da Pergunta */}
                  <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", marginBottom: "1.5rem" }}>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label className="form-label">Pergunta Principal</label>
                      <input 
                        className="form-input" 
                        value={item.pergunta} 
                        onChange={(e) => handleChangeQuestion(qIndex, "pergunta", e.target.value)} 
                        placeholder="Ex: Como emitir a segunda via?" 
                        style={{ fontWeight: 600 }}
                      />
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", paddingBottom: "0.8rem", fontWeight: 500 }}>
                      <input 
                        type="checkbox" 
                        checked={item.ativo} 
                        onChange={(e) => handleChangeQuestion(qIndex, "ativo", e.target.checked)} 
                        style={{ width: "18px", height: "18px" }}
                      />
                      Ativo
                    </label>
                    <button className="btn btn-danger" onClick={() => handleRemoveQuestion(qIndex)} style={{ height: "42px" }}>
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {/* Lista de Respostas */}
                  <div style={{ background: "#f8fafc", padding: "1rem", borderRadius: "var(--radius)" }}>
                    <div className="section-title" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: "1rem" }}>
                      <MessageCircle size={16} />
                      <h3 style={{ fontSize: "0.9rem" }}>Respostas / Passos</h3>
                    </div>

                    {item.respostas.map((resposta, aIndex) => (
                      <div key={aIndex} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                        <input 
                          className="form-input" 
                          value={resposta} 
                          onChange={(e) => handleChangeAnswer(qIndex, aIndex, e.target.value)} 
                          placeholder="Digite a resposta ou um passo a passo..." 
                        />
                        <button className="btn btn-secondary" onClick={() => handleRemoveAnswer(qIndex, aIndex)} title="Remover linha">
                          <Trash2 size={16} color="var(--color-danger)" />
                        </button>
                      </div>
                    ))}

                    <button className="btn btn-secondary btn-sm" onClick={() => handleAddQuestion(qIndex)} style={{ marginTop: "0.5rem" }}>
                      <Plus size={14} /> Adicionar linha de resposta
                    </button>
                  </div>

                </div>
              ))}
            </div>
          )}

          <div className="config-footer">
            <button className="btn btn-primary" disabled={isLoading} onClick={handleSave}>
              {isLoading ? (
                <><Loader2 className="spinner" size={18} /> Salvando...</>
              ) : (
                <><Save size={18} /> Salvar FAQ</>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}